<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">批次匯入訂單</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="handleViewHistory">
          <i class="ri-history-line"></i>
          匯入紀錄
        </button>
        <button class="btn btn-secondary" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          返回列表
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="import-container">
      <div class="import-points-notice">
        <div v-if="importPointsMode === 'orders_only'" class="import-points-notice__manual">
          <i class="ri-information-line"></i>
          <p>已選擇不套用給點規則。請於步驟 2 映射「匯入點數」欄位；有填數字則以檔案為準（含 0），未映射或空白則不發點。</p>
        </div>

        <template v-else>
        <div v-if="activeRuleLoading" class="import-points-notice__loading">
          <span class="spinner spinner-sm"></span>
          載入給點規則中...
        </div>

        <div v-else-if="!activeRule" class="import-points-notice__empty">
          <i class="ri-error-warning-line"></i>
          <div>
            <strong>目前無生效中的消費給點規則</strong>
            <p>匯入訂單將不會發放消費點數。請至常態給點規則設定並啟用。</p>
            <router-link to="/client/points/earning-rules/normal" class="import-points-notice__link">
              前往設定給點規則
            </router-link>
          </div>
        </div>

        <div v-else class="import-points-notice__card">
          <div class="import-points-notice__header">
            <i class="ri-coin-line"></i>
            <h3 class="import-points-notice__title">目前生效的消費給點規則</h3>
            <span
              class="rule-type-badge"
              :class="activeRule.ruleType === 'time_range' ? 'rule-type-badge--time' : 'rule-type-badge--normal'"
            >
              {{ activeRule.ruleType === 'time_range' ? '時間區間' : '常態' }}
            </span>
          </div>

          <div v-if="activeRule.ruleType === 'time_range'" class="import-points-notice__period">
            <span class="import-points-notice__rule-name">{{ activeRule.ruleName }}</span>
            <span class="import-points-notice__rule-period">
              <i class="ri-time-line"></i>
              {{ formatRuleDateTime(activeRule.startTime) }}
              ～
              {{ formatRuleDateTime(activeRule.endTime) }}
            </span>
          </div>

          <div class="import-points-notice__details">
            <div class="import-points-notice__detail">
              <span class="import-points-notice__label">會員等級回饋率</span>
              <div class="import-points-notice__tier-list">
                <span
                  v-for="(rate, tier) in activeRule.tierConfig"
                  :key="tier"
                  class="import-points-notice__tier-badge"
                >
                  {{ getTierName(tier) }}：{{ rate }}%
                </span>
                <span v-if="!hasTierRates(activeRule.tierConfig)" class="import-points-notice__empty-hint">
                  尚未設定回饋率
                </span>
              </div>
            </div>
            <div class="import-points-notice__detail">
              <span class="import-points-notice__label">給點計算</span>
              <span class="import-points-notice__calc">
                每 {{ activeRule.baseAmount || 1 }} 元・{{ getRoundingMethodLabel(activeRule.roundingMethod) }}・{{ formatEffectiveDays(activeRule.effectiveDays) }}
              </span>
            </div>
          </div>

          <p v-if="importPointsMode === 'apply_rules'" class="import-points-notice__reminder">
            已選擇套用給點規則：成功匯入且已對應會員的訂單將依上列規則計算點數，檔案中的點數欄位將被忽略。
            未對應會員的訂單將以非會員交易匯入（不發點，可後續綁定會員）。訂單建立失敗，或依訂單日期無適用規則時，點數為 0。
            <router-link
              :to="activeRule.ruleType === 'time_range' ? '/client/points/earning-rules/time-range' : '/client/points/earning-rules/normal'"
              class="import-points-notice__link"
            >
              管理給點規則
            </router-link>
          </p>
        </div>
        </template>
      </div>

      <!-- 步驟 1: 上傳檔案 -->
      <div v-if="currentStep === 1" class="step-section">
        <h2 class="step-title">步驟 1：上傳檔案</h2>

        <div class="import-options">
          <h3 class="import-options__title">點數處理方式</h3>
          <div class="import-options__choices">
            <label class="import-options__choice">
              <input
                v-model="importPointsMode"
                type="radio"
                value="apply_rules"
              />
              <span class="import-options__choice-body">
                <strong>套用消費給點規則</strong>
                <span class="import-options__hint">
                  依目前給點規則計算點數，檔案中的點數欄位將被忽略。
                </span>
              </span>
            </label>
            <label class="import-options__choice">
              <input
                v-model="importPointsMode"
                type="radio"
                value="orders_only"
              />
              <span class="import-options__choice-body">
                <strong>不套用給點規則</strong>
                <span class="import-options__hint">
                  僅匯入訂單；可於步驟 2 映射「匯入點數」欄位，有填則以檔案為準。
                </span>
              </span>
            </label>
          </div>
        </div>
        
        <div class="upload-area" :class="{ 'dragover': isDragging }" 
             @drop.prevent="handleDrop" 
             @dragover.prevent="isDragging = true"
             @dragleave.prevent="isDragging = false">
          <input
            ref="fileInput"
            type="file"
            accept=".csv,.xlsx,.xls,.json"
            @change="handleFileSelect"
            style="display: none"
          />
          
          <div v-if="!uploadData" class="upload-placeholder">
            <i class="ri-file-upload-line upload-icon"></i>
            <p>拖放檔案到這裡，或點擊選擇檔案</p>
            <p class="upload-hint">支援格式：CSV、Excel (.xlsx, .xls)、JSON</p>
            <p class="upload-hint upload-hint--sub">
              <template v-if="importPointsMode === 'apply_rules'">
                已選擇套用給點規則，檔案中的點數欄位將被忽略。
              </template>
              <template v-else>
                已選擇不套用給點規則，可於步驟 2 映射「匯入點數」欄位。
              </template>
            </p>
            <div class="upload-actions">
              <button class="btn btn-primary" @click="$refs.fileInput.click()">
                選擇檔案
              </button>
              <a
                href="/sample_orders_import.xlsx"
                download="訂單匯入範例.xlsx"
                class="btn btn-secondary"
              >
                <i class="ri-download-line"></i>
                下載 Excel 範例
              </a>
              <a
                href="/sample_orders_import.csv"
                download="訂單匯入範例.csv"
                class="btn btn-secondary"
              >
                <i class="ri-download-line"></i>
                下載 CSV 範例
              </a>
              <a
                href="/sample_orders_import.json"
                download="訂單匯入範例.json"
                class="btn btn-secondary"
              >
                <i class="ri-download-line"></i>
                下載 JSON 範例
              </a>
            </div>
            <p class="upload-hint upload-hint--sub">
              CSV 為 UTF-8 並含 BOM。若 Excel 直接開啟仍亂碼，請用「資料 → 取得資料 → 從檔案 → 從文字／CSV」匯入，編碼選 UTF-8；或改用上方 Excel 範例。
            </p>
            <p class="upload-hint upload-hint--sub">
              JSON 可為 <code>{ "orders": [ … ] }</code>，或直接為訂單物件陣列 <code>[ … ]</code>；物件鍵名即為偵測到的欄位名，請在步驟 2 對應到系統欄位（與 Excel／CSV 範例欄位意義相同）。
            </p>
          </div>
          
          <div v-else class="upload-info">
            <i class="ri-file-line file-icon"></i>
            <div class="file-details">
              <p class="file-name">{{ uploadData.fileName }}</p>
              <p class="file-size">檔案大小：{{ formatFileSize(uploadData.fileSize) }}</p>
              <p class="file-rows">偵測到 {{ uploadData.detectedRows }} 筆資料</p>
            </div>
            <button class="btn btn-sm btn-danger" @click="handleRemoveFile">
              <i class="ri-delete-bin-line"></i>
              移除
            </button>
          </div>
        </div>

        <div v-if="uploadError" class="error-message">
          {{ uploadError }}
        </div>

        <div class="step-actions">
          <button 
            class="btn btn-primary" 
            :disabled="!uploadData || uploading"
            @click="handleNextStep"
          >
            {{ uploading ? '上傳中...' : '下一步：設定欄位映射' }}
          </button>
        </div>
      </div>

      <!-- 步驟 2: 欄位映射 -->
      <div v-if="currentStep === 2" class="step-section">
        <h2 class="step-title">步驟 2：設定欄位映射</h2>
        
        <div class="mapping-info">
          <p>請將檔案中的欄位對應到系統欄位。名稱相同的欄位已自動對應，其餘請手動選擇。</p>
          <p v-if="importPointsMode === 'orders_only'" class="mapping-info__hint">
            已選擇不套用給點規則，可選擇映射「匯入點數」欄位；有填數字則以檔案為準（含 0），未映射或空白則不發點。
          </p>
        </div>

        <div class="mapping-table">
          <table>
            <thead>
              <tr>
                <th>系統欄位</th>
                <th>檔案欄位</th>
                <th>說明</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="field in mappingFields" :key="field.key">
                <td>
                  <span class="field-name">{{ field.label }}</span>
                  <span v-if="field.required" class="required-badge">必填</span>
                </td>
                <td>
                  <select 
                    v-model="fieldMapping[field.key]" 
                    class="form-control"
                    :required="field.required"
                  >
                    <option value="">請選擇檔案欄位</option>
                    <option 
                      v-for="col in detectedColumns" 
                      :key="col" 
                      :value="col"
                    >
                      {{ col }}
                    </option>
                  </select>
                </td>
                <td class="field-desc">{{ field.description }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="step-actions">
          <button class="btn btn-secondary" @click="handlePrevStep">上一步</button>
          <button 
            class="btn btn-primary" 
            :disabled="!isMappingValid || mappingLoading"
            @click="handleSaveMapping"
          >
            {{ mappingLoading ? '儲存中...' : '下一步：預覽與驗證' }}
          </button>
        </div>
      </div>

      <!-- 步驟 3: 預覽與驗證 -->
      <div v-if="currentStep === 3" class="step-section">
        <h2 class="step-title">步驟 3：預覽與驗證</h2>
        
        <div v-if="previewLoading" class="loading-state">
          <div class="spinner"></div>
          <p>驗證中...</p>
        </div>

        <div v-else-if="previewData">
          <div class="validation-summary">
            <h3>匯入預估</h3>
            <p class="validation-summary__lead">
              整份檔案共 <strong>{{ previewData.validationSummary.totalRows }}</strong> 筆，
              預計匯入 <strong class="text-success">{{ previewSummary.importableRows }}</strong> 筆。
            </p>
            <ul v-if="previewSummary.hasNotes" class="validation-summary__notes">
              <li v-if="previewSummary.skippedInvalidRows > 0">
                格式錯誤將跳過：<strong>{{ previewSummary.skippedInvalidRows }}</strong> 筆
              </li>
              <li v-if="previewSummary.skippedDuplicateRows > 0">
                重複訂單將跳過：<strong>{{ previewSummary.skippedDuplicateRows }}</strong> 筆（含系統既有或檔案內重複編號）
              </li>
              <li v-if="previewSummary.guestOrderRows > 0">
                其中非會員交易：<strong>{{ previewSummary.guestOrderRows }}</strong> 筆（仍會建立訂單，不發點）
              </li>
            </ul>
            <p class="validation-summary__hint">
              下方表格僅顯示前 10 筆預覽；「匯入狀態」欄位供核對單筆是否會寫入。
            </p>
          </div>

          <div class="preview-table">
            <h3>資料預覽（前 10 筆）</h3>
            <div class="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>行號</th>
                    <th>訂單編號</th>
                    <th>訂單日期</th>
                    <th>會員識別</th>
                    <th>總金額</th>
                    <th v-if="importPointsMode === 'orders_only'">匯入點數</th>
                    <th>匯入狀態</th>
                    <th>會員匹配</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in previewData.preview" :key="item.rowNumber">
                    <td>{{ item.rowNumber }}</td>
                    <td>{{ item.data.orderNumber || '-' }}</td>
                    <td>{{ formatDate(item.data.orderDate) }}</td>
                    <td>{{ item.data.customerIdentifier || '-' }}</td>
                    <td>{{ formatCurrency(item.data.totalAmount) }}</td>
                    <td v-if="importPointsMode === 'orders_only'" class="preview-points-cell">
                      {{ formatPreviewImportedPoints(item.data) }}
                    </td>
                    <td>
                      <span :class="getPreviewImportStatusClass(item)">
                        {{ getPreviewImportStatusLabel(item) }}
                      </span>
                    </td>
                    <td>
                      <span :class="item.customerMatch.matched ? 'badge badge-success' : 'badge badge-secondary'">
                        {{ item.customerMatch.matched ? '已匹配' : '非會員' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="step-actions">
          <button class="btn btn-secondary" @click="handlePrevStep">上一步</button>
          <button 
            class="btn btn-primary" 
            :disabled="!previewData || executing"
            @click="handleExecuteImport"
          >
            {{ executing ? '執行中...' : '執行匯入' }}
          </button>
        </div>
      </div>

      <!-- 步驟 4: 執行結果 -->
      <div v-if="currentStep === 4" class="step-section">
        <h2 class="step-title">步驟 4：匯入結果</h2>
        
        <div v-if="jobStatus" class="result-container">
          <div class="status-card" :class="getStatusClass(jobStatus.status)">
            <h3>匯入狀態：{{ getStatusText(jobStatus.status) }}</h3>
            <div v-if="jobStatus.status === 'processing'" class="progress-bar">
              <div class="progress-fill" :style="{ width: `${jobStatus.progress || 0}%` }"></div>
            </div>
            <p v-if="jobStatus.status === 'processing'">進度：{{ jobStatus.progress || 0 }}%</p>
          </div>

          <div class="result-summary">
            <h3>匯入統計</h3>
            <div class="summary-grid">
              <div class="summary-item">
                <span class="summary-label">總處理筆數</span>
                <span class="summary-value">{{ jobStatus.totalProcessed || 0 }}</span>
              </div>
              <div class="summary-item success">
                <span class="summary-label">成功</span>
                <span class="summary-value">{{ jobStatus.successCount || 0 }}</span>
              </div>
              <div class="summary-item error">
                <span class="summary-label">失敗</span>
                <span class="summary-value">{{ jobStatus.failedCount || 0 }}</span>
              </div>
              <div class="summary-item warning">
                <span class="summary-label">跳過</span>
                <span class="summary-value">{{ jobStatus.skippedCount || 0 }}</span>
              </div>
              <div class="summary-item info">
                <span class="summary-label">發放點數</span>
                <span class="summary-value">{{ formatNumber(jobStatus.totalPointsAwarded || 0) }}</span>
              </div>
            </div>
          </div>

          <div v-if="jobStatus.status === 'processing'" class="polling-info">
            <p>匯入正在背景執行中，您可以關閉此頁面，稍後再查看結果。</p>
            <button class="btn btn-secondary" @click="pollJobStatus">重新整理狀態</button>
          </div>

          <div v-if="jobStatus.status !== 'processing'" class="step-actions">
            <button class="btn btn-primary" @click="handleViewHistory">返回匯入紀錄</button>
            <button v-if="jobStatus.failedCount > 0" class="btn btn-secondary" @click="handleViewErrors">
              查看錯誤詳情
            </button>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { orderImportService } from '@/api/modules/orderImport.js';
import { pointsEarningRulesService } from '@/api/modules/pointsEarningRules.js';
import { memberTierService } from '@/api/modules/memberTiers.js';
import { showSuccess, showError } from '@/utils/swal.js';

export default {
  name: 'ClientOrderImport',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('批次匯入訂單');
    
    const currentStep = ref(1);
    const isDragging = ref(false);
    const uploading = ref(false);
    const mappingLoading = ref(false);
    const previewLoading = ref(false);
    const executing = ref(false);
    
    const uploadData = ref(null);
    const uploadError = ref(null);
    const detectedColumns = ref([]);
    const fieldMapping = ref({});
    const mappingId = ref(null);
    const previewData = ref(null);
    const jobId = ref(null);
    const jobStatus = ref(null);
    const activeRule = ref(null);
    const activeRuleLoading = ref(false);
    const tiers = ref([]);
    const importPointsMode = ref('apply_rules');
    const pollingInterval = ref(null);

    const allMappingFields = [
      { key: 'orderNumber', label: '訂單編號', description: '訂單的唯一識別編號', required: true },
      { key: 'orderDate', label: '訂單日期', description: '訂單建立的日期時間', required: true },
      { key: 'customerIdentifier', label: '會員識別', description: '會員 Email 或手機號碼', required: true },
      { key: 'totalAmount', label: '訂單總金額', description: '訂單的總金額', required: true },
      { key: 'subtotal', label: '小計', description: '訂單小計金額（選填）', required: false },
      { key: 'discountAmount', label: '折扣金額', description: '折扣金額（選填）', required: false },
      { key: 'customerName', label: '會員姓名', description: '會員姓名（選填）', required: false },
      { key: 'paymentMethod', label: '付款方式', description: '付款方式（選填）', required: false },
      {
        key: 'pointsEarned',
        label: '匯入點數',
        description: '選填。有對應欄且儲存格有值時採用檔案點數（含 0）；留空或未映射則不發點',
        required: false
      }
    ];

    const mappingFields = computed(() => {
      if (importPointsMode.value === 'apply_rules') {
        return allMappingFields.filter((field) => field.key !== 'pointsEarned');
      }
      return allMappingFields;
    });

    const buildFieldMappingForSave = () => {
      const mapping = { ...fieldMapping.value };
      if (importPointsMode.value === 'apply_rules') {
        delete mapping.pointsEarned;
      }
      return mapping;
    };

    const applyAutoFieldMapping = () => {
      const columns = (detectedColumns.value || [])
        .map((col) => String(col ?? '').trim())
        .filter(Boolean);
      if (!columns.length) return;

      const columnSet = new Set(columns);
      const next = { ...fieldMapping.value };

      for (const field of mappingFields.value) {
        if (next[field.key]) continue;
        if (columnSet.has(field.key)) {
          next[field.key] = field.key;
        } else if (columnSet.has(field.label)) {
          next[field.key] = field.label;
        }
      }

      fieldMapping.value = next;
    };

    const isMappingValid = computed(() => {
      return mappingFields.value
        .filter((f) => f.required)
        .every((f) => fieldMapping.value[f.key]);
    });

    const previewSummary = computed(() => {
      const summary = previewData.value?.validationSummary || {};
      const skippedInvalidRows = summary.skippedInvalidRows ?? summary.invalidRows ?? 0;
      const skippedDuplicateRows = summary.skippedDuplicateRows ?? summary.duplicates ?? 0;
      const importableRows = summary.importableRows ?? 0;
      const guestOrderRows = summary.guestOrderRows ?? 0;
      return {
        importableRows,
        skippedInvalidRows,
        skippedDuplicateRows,
        guestOrderRows,
        hasNotes: skippedInvalidRows > 0 || skippedDuplicateRows > 0 || guestOrderRows > 0
      };
    });

    const getPreviewImportStatusLabel = (item) => {
      if (!item?.validation?.valid) return '將跳過（格式錯誤）';
      if (item?.duplicateCheck?.isDuplicate) {
        if (item.duplicateCheck.fileDuplicate && item.duplicateCheck.systemDuplicate) {
          return '將跳過（重複訂單）';
        }
        if (item.duplicateCheck.fileDuplicate) return '將跳過（檔案內重複）';
        return '將跳過（系統重複）';
      }
      return '將匯入';
    };

    const getPreviewImportStatusClass = (item) => {
      if (!item?.validation?.valid || item?.duplicateCheck?.isDuplicate) {
        return 'badge badge-danger';
      }
      return 'badge badge-success';
    };

    watch(importPointsMode, (mode) => {
      if (mode === 'apply_rules') {
        delete fieldMapping.value.pointsEarned;
      } else {
        applyAutoFieldMapping();
      }
    });

    const handleFileSelect = (event) => {
      const file = event.target.files[0];
      if (file) {
        handleFile(file);
      }
    };

    const handleDrop = (event) => {
      isDragging.value = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        handleFile(file);
      }
    };

    const handleFile = async (file) => {
      uploadError.value = null;
      
      // 判斷檔案格式
      const fileName = file.name.toLowerCase();
      let format = 'csv';
      if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        format = 'excel';
      } else if (fileName.endsWith('.json')) {
        format = 'json';
      } else if (!fileName.endsWith('.csv')) {
        uploadError.value = '不支援的檔案格式，請上傳 CSV、Excel 或 JSON 檔案';
        return;
      }

      uploadData.value = {
        file: file,
        fileName: file.name,
        fileSize: file.size,
        format: format
      };

      // 自動上傳檔案
      await uploadFile();
    };

    const uploadFile = async () => {
      if (!uploadData.value) return;

      uploading.value = true;
      uploadError.value = null;

      try {
        const response = await orderImportService.uploadFile(
          uploadData.value.file,
          uploadData.value.format
        );

        if (response && response.success && response.data) {
          uploadData.value = {
            ...uploadData.value,
            ...response.data
          };
          detectedColumns.value = response.data.detectedColumns || [];
          applyAutoFieldMapping();
        } else {
          uploadError.value = response?.message || '上傳失敗';
        }
      } catch (err) {
        console.error('上傳檔案失敗:', err);
        uploadError.value = err.message || '上傳檔案失敗';
        await showError('上傳失敗', uploadError.value);
      } finally {
        uploading.value = false;
      }
    };

    const handleRemoveFile = () => {
      uploadData.value = null;
      detectedColumns.value = [];
      fieldMapping.value = {};
      uploadError.value = null;
    };

    const handleNextStep = () => {
      if (currentStep.value === 1 && uploadData.value) {
        applyAutoFieldMapping();
        currentStep.value = 2;
      }
    };

    const handlePrevStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--;
      }
    };

    const handleSaveMapping = async () => {
      if (!isMappingValid.value) {
        await showError('驗證失敗', '請完成所有必填欄位的映射');
        return;
      }

      mappingLoading.value = true;

      try {
        const response = await orderImportService.setFieldMapping(
          uploadData.value.uploadId,
          buildFieldMappingForSave(),
          true
        );

        if (response && response.success && response.data) {
          mappingId.value = response.data.mappingId;
          if (response.data.fieldMapping) {
            fieldMapping.value = { ...response.data.fieldMapping };
          }
          currentStep.value = 3;
          await loadPreview();
        } else {
          await showError('儲存失敗', response?.message || '儲存欄位映射失敗');
        }
      } catch (err) {
        console.error('儲存欄位映射失敗:', err);
        await showError('儲存失敗', err.message || '儲存欄位映射失敗');
      } finally {
        mappingLoading.value = false;
      }
    };

    const loadPreview = async () => {
      if (!uploadData.value || !mappingId.value) return;

      previewLoading.value = true;

      try {
        const response = await orderImportService.previewData(
          uploadData.value.uploadId,
          mappingId.value,
          10
        );

        if (response && response.success && response.data) {
          previewData.value = response.data;
        } else {
          await showError('預覽失敗', response?.message || '預覽資料失敗');
        }
      } catch (err) {
        console.error('預覽資料失敗:', err);
        await showError('預覽失敗', err.message || '預覽資料失敗');
      } finally {
        previewLoading.value = false;
      }
    };

    const handleExecuteImport = async () => {
      if (!uploadData.value || !mappingId.value) {
        await showError('驗證失敗', '請先完成檔案上傳和欄位映射');
        return;
      }

      if (!uploadData.value.uploadId) {
        await showError('驗證失敗', '缺少上傳 ID，請重新上傳檔案');
        return;
      }

      executing.value = true;

      try {
        const response = await orderImportService.executeImport(
          uploadData.value.uploadId,
          mappingId.value,
          {
            duplicateStrategy: 'skip',
            unmatchedCustomerStrategy: 'create_pending',
            autoCalculatePoints: importPointsMode.value === 'apply_rules',
            pointsMultiplier: 1.0,
            skipInvalidRows: true
          }
        );

        if (response && response.success && response.data) {
          jobId.value = response.data.id;
          currentStep.value = 4;
          await pollJobStatus();
          startPolling();
        } else {
          await showError('執行失敗', response?.message || '執行匯入失敗');
        }
      } catch (err) {
        console.error('執行匯入失敗:', err);
        await showError('執行失敗', err.message || '執行匯入失敗');
      } finally {
        executing.value = false;
      }
    };

    const pollJobStatus = async () => {
      if (!jobId.value) return;

      try {
        const response = await orderImportService.getJobStatus(jobId.value);
        if (response && response.success && response.data) {
          jobStatus.value = response.data;
          
          if (response.data.status === 'completed' || response.data.status === 'failed') {
            stopPolling();
          }
        }
      } catch (err) {
        console.error('查詢作業狀態失敗:', err);
      }
    };

    const startPolling = () => {
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value);
      }
      pollingInterval.value = setInterval(() => {
        pollJobStatus();
      }, 3000); // 每 3 秒輪詢一次
    };

    const stopPolling = () => {
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value);
        pollingInterval.value = null;
      }
    };

    const handleViewErrors = () => {
      // TODO: 實作查看錯誤詳情
      router.push(`/client/orders/import/errors/${jobId.value}`);
    };

    const handleBack = () => {
      stopPolling();
      router.push('/client/orders');
    };

    const handleViewHistory = () => {
      stopPolling();
      router.push('/client/orders/import/history');
    };

    const formatFileSize = (bytes) => {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const formatCurrency = (amount) => {
      if (amount === null || amount === undefined) return 'NT$ 0';
      return `NT$ ${Number(amount).toLocaleString('zh-TW')}`;
    };

    const formatNumber = (number) => {
      if (number === null || number === undefined) return '0';
      return Number(number).toLocaleString('zh-TW');
    };

    /** 預覽表：不套用規則時顯示檔案點數 */
    const formatPreviewImportedPoints = (data) => {
      if (!data || !Object.prototype.hasOwnProperty.call(data, 'pointsEarned')) {
        return '不發點';
      }
      const raw = data.pointsEarned;
      if (raw === null || raw === undefined) return '不發點';
      if (typeof raw === 'string' && raw.trim() === '') return '不發點';
      return String(raw).trim();
    };

    const getStatusClass = (status) => {
      const statusMap = {
        pending: 'status-pending',
        processing: 'status-processing',
        completed: 'status-completed',
        failed: 'status-failed',
        partial: 'status-partial',
      };
      return statusMap[status] || '';
    };

    const getStatusText = (status) => {
      const statusMap = {
        pending: '待處理',
        processing: '處理中',
        completed: '已完成',
        failed: '失敗',
        partial: '部分成功',
      };
      return statusMap[status] || status;
    };

    const fetchActiveRule = async () => {
      activeRuleLoading.value = true;
      try {
        const response = await pointsEarningRulesService.getActiveRule();
        if (response && response.success) {
          activeRule.value = response.data || null;
        }
      } catch (err) {
        console.error('獲取生效給點規則失敗:', err);
      } finally {
        activeRuleLoading.value = false;
      }
    };

    const fetchTiers = async () => {
      try {
        const response = await memberTierService.getTiers({ isActive: true });
        if (response && response.success && response.data) {
          tiers.value = response.data;
        }
      } catch (err) {
        console.error('獲取會員等級列表失敗:', err);
      }
    };

    const getTierName = (tierCode) => {
      const tier = tiers.value.find((t) => t.tierCode === tierCode);
      return tier ? tier.tierName : tierCode;
    };

    const getRoundingMethodLabel = (method) => {
      const map = { round: '四捨五入', ceil: '無條件進位', floor: '無條件捨去' };
      return map[method] || '無條件捨去';
    };

    const hasTierRates = (tierConfig) => {
      if (!tierConfig || typeof tierConfig !== 'object') return false;
      return Object.values(tierConfig).some(
        (rate) => rate !== null && rate !== undefined && rate !== ''
      );
    };

    const formatEffectiveDays = (days) => {
      const value = Number(days) || 0;
      return value > 0 ? `購買後 ${value} 天生效` : '立即生效';
    };

    const formatRuleDateTime = (dateTime) => {
      if (!dateTime) return '-';
      const date = new Date(dateTime);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    onMounted(async () => {
      await Promise.all([fetchTiers(), fetchActiveRule()]);
    });

    onUnmounted(() => {
      stopPolling();
    });

    return {
      pageTitle,
      currentStep,
      isDragging,
      uploading,
      mappingLoading,
      previewLoading,
      executing,
      uploadData,
      uploadError,
      detectedColumns,
      fieldMapping,
      mappingFields,
      isMappingValid,
      previewData,
      previewSummary,
      jobStatus,
      activeRule,
      activeRuleLoading,
      importPointsMode,
      getTierName,
      getRoundingMethodLabel,
      hasTierRates,
      formatEffectiveDays,
      formatRuleDateTime,
      handleFileSelect,
      handleDrop,
      handleRemoveFile,
      handleNextStep,
      handlePrevStep,
      handleSaveMapping,
      handleExecuteImport,
      pollJobStatus,
      handleViewErrors,
      handleBack,
      handleViewHistory,
      formatFileSize,
      formatDate,
      formatCurrency,
      formatNumber,
      formatPreviewImportedPoints,
      getPreviewImportStatusLabel,
      getPreviewImportStatusClass,
      getStatusClass,
      getStatusText,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.import-points-notice__manual {
  display: flex;
  gap: 10px;
  padding: 14px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #64748b;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.65;
  color: #475569;
}

.import-points-notice__manual i {
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 1px;
  color: #64748b;
}

.import-points-notice__manual p {
  margin: 0;
}

.import-points-notice {
  margin-bottom: 20px;
}

.import-points-notice__loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  color: #64748b;
  font-size: 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.import-points-notice__empty {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-left: 4px solid #f59e0b;
  border-radius: 8px;
  color: #78350f;
  font-size: 14px;
  line-height: 1.6;
}

.import-points-notice__empty i {
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
}

.import-points-notice__empty p {
  margin: 4px 0 8px;
}

.import-points-notice__card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #3b82f6;
  border-radius: 8px;
  padding: 16px;
}

.import-points-notice__header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.import-points-notice__header i {
  font-size: 18px;
  color: #3b82f6;
}

.import-points-notice__title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}

.rule-type-badge {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 500;
}

.rule-type-badge--normal {
  background: #dbeafe;
  color: #1d4ed8;
}

.rule-type-badge--time {
  background: #dcfce7;
  color: #15803d;
}

.import-points-notice__period {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #475569;
}

.import-points-notice__rule-name {
  font-weight: 600;
  color: #334155;
}

.import-points-notice__rule-period {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.import-points-notice__details {
  display: grid;
  gap: 10px;
  margin-bottom: 12px;
}

.import-points-notice__detail {
  font-size: 13px;
  line-height: 1.6;
}

.import-points-notice__label {
  display: block;
  font-weight: 600;
  color: #334155;
  margin-bottom: 4px;
}

.import-points-notice__tier-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.import-points-notice__tier-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  color: #475569;
  font-size: 12px;
}

.import-points-notice__empty-hint {
  color: #94a3b8;
  font-size: 12px;
}

.import-points-notice__calc {
  color: #475569;
}

.import-points-notice__reminder {
  margin: 0;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
  font-size: 12px;
  line-height: 1.65;
  color: #64748b;
}

.import-points-notice__link {
  display: inline-block;
  margin-top: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #2563eb;
  text-decoration: none;
}

.import-points-notice__link:hover {
  text-decoration: underline;
}

.import-container {
  max-width: 1200px;
}

.step-section {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.step-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 24px;
  color: #333;
}

.upload-area {
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  transition: all 0.3s;
  cursor: pointer;
}

.upload-area.dragover {
  border-color: #007bff;
  background: #f0f8ff;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.upload-actions {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
}

.upload-icon {
  font-size: 48px;
  color: #999;
}

.upload-hint {
  color: #666;
  font-size: 14px;
}

.upload-hint--sub {
  margin-top: -8px;
  margin-bottom: 0;
  font-size: 12px;
  color: #64748b;
  max-width: 520px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
}

.upload-hint--sub + .upload-hint--sub {
  margin-top: 10px;
}

.upload-hint--sub code {
  font-size: 11px;
  padding: 1px 5px;
  background: #f1f5f9;
  border-radius: 4px;
  color: #334155;
}

.upload-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.file-icon {
  font-size: 32px;
  color: #007bff;
}

.file-details {
  flex: 1;
  text-align: left;
}

.file-name {
  font-weight: 600;
  margin-bottom: 4px;
}

.file-size,
.file-rows {
  font-size: 14px;
  color: #666;
}

.mapping-table {
  margin-top: 24px;
}

.mapping-table table {
  width: 100%;
  border-collapse: collapse;
}

.mapping-table th,
.mapping-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.mapping-table th {
  background: #f8f9fa;
  font-weight: 600;
}

.field-name {
  font-weight: 500;
}

.required-badge {
  background: #dc3545;
  color: #fff;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  margin-left: 8px;
}

.field-desc {
  color: #666;
  font-size: 14px;
}

.mapping-info__hint {
  margin-top: 8px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
}

.import-options {
  margin-bottom: 20px;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.import-options__title {
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 600;
  color: #334155;
}

.import-options__choices {
  display: grid;
  gap: 10px;
}

.import-options__choice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
}

.import-options__choice:has(input:checked) {
  border-color: #3b82f6;
  background: #eff6ff;
}

.import-options__choice input {
  margin-top: 3px;
  flex-shrink: 0;
}

.import-options__choice-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 14px;
  color: #334155;
}

.import-options__hint {
  font-size: 12px;
  line-height: 1.6;
  color: #64748b;
  font-weight: 400;
}

.validation-summary,
.result-summary {
  margin-bottom: 24px;
}

.validation-summary__lead {
  margin: 12px 0 0;
  font-size: 15px;
  color: #334155;
  line-height: 1.7;
}

.validation-summary__lead .text-success {
  color: #198754;
}

.validation-summary__notes {
  margin: 12px 0 0;
  padding-left: 20px;
  color: #475569;
  font-size: 14px;
  line-height: 1.8;
}

.validation-summary__notes strong {
  color: #1e293b;
}

.validation-summary__hint {
  margin: 12px 0 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.summary-item {
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  text-align: center;
}

.summary-item.success {
  background: #d4edda;
}

.summary-item.error {
  background: #f8d7da;
}

.summary-item.warning {
  background: #fff3cd;
}

.summary-item.info {
  background: #d1ecf1;
}

.summary-label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.summary-value {
  display: block;
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.preview-table {
  margin-top: 24px;
}

.table-wrapper {
  overflow-x: auto;
  margin-top: 16px;
}

.table-wrapper table {
  width: 100%;
  min-width: 800px;
  border-collapse: collapse;
}

.table-wrapper th,
.table-wrapper td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.table-wrapper th {
  background: #f8f9fa;
  font-weight: 600;
}

.status-card {
  padding: 24px;
  border-radius: 8px;
  margin-bottom: 24px;
  text-align: center;
}

.status-card.status-processing {
  background: #d1ecf1;
}

.status-card.status-completed {
  background: #d4edda;
}

.status-card.status-failed {
  background: #f8d7da;
}

.progress-bar {
  width: 100%;
  height: 24px;
  background: #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  margin: 16px 0;
}

.progress-fill {
  height: 100%;
  background: #007bff;
  transition: width 0.3s;
}

.step-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
}

.error-message {
  color: #dc3545;
  margin-top: 16px;
  padding: 12px;
  background: #f8d7da;
  border-radius: 4px;
}
</style>
